import { useState, useEffect, useCallback } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  User, Mail, Phone, Shield, Building2, CreditCard, Copy, Check, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
import { BO_BASE } from '@/utils/getBasePath';
import { copyText } from '@/lib/copyText';

export default function UserProfile() {
  const ctx = useOutletContext() || {};
  const { token, manager, restaurant, activeRestaurantId } = ctx;

  const [profile, setProfile] = useState(() => ({
    full_name: manager?.full_name || manager?.name || 'User',
    email: manager?.email || '',
    phone: manager?.phone || '',
    role: manager?.role || 'owner',
    restaurant_name: restaurant?.name || '',
  }));
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [copied, setCopied] = useState(null);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');

  const tenantId = activeRestaurantId || restaurant?.id || manager?.restaurant_id;

  // Fetch user profile from backend
  const fetchProfile = useCallback(async () => {
    setLoadingProfile(true);
    try {
      const res = await fetch(`${API}/api/merchant/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const mgr = data.manager || data;
        const rest = data.restaurant || {};
        setProfile({
          full_name: mgr.full_name || mgr.name || 'User',
          email: mgr.email || '',
          phone: mgr.phone || mgr.phone_number || '',
          role: mgr.role || 'owner',
          restaurant_name: rest.name || restaurant?.name || '',
          restaurant_id: data.active_restaurant_id || mgr.restaurant_id,
        });
        setPhoneValue(mgr.phone || mgr.phone_number || '');
      } else {
        // Fallback to manager context
        setProfile({
          full_name: manager?.full_name || manager?.name || 'User',
          email: manager?.email || '',
          phone: manager?.phone || '',
          role: manager?.role || 'owner',
          restaurant_name: restaurant?.name || '',
        });
        setPhoneValue(manager?.phone || '');
      }
    } catch {
      setProfile({
        full_name: manager?.full_name || manager?.name || 'User',
        email: manager?.email || '',
        phone: manager?.phone || '',
        role: manager?.role || 'owner',
        restaurant_name: restaurant?.name || '',
      });
      setPhoneValue(manager?.phone || '');
    } finally {
      setLoadingProfile(false);
    }
  }, [token, manager, restaurant]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  // Update phone
  const handleSavePhone = async () => {
    try {
      const res = await fetch(`${API}/api/merchant/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phone: phoneValue }),
      });
      if (res.ok) {
        toast.success('Phone number updated');
        setEditingPhone(false);
        fetchProfile();
      } else {
        toast.error('Failed to update phone');
      }
    } catch {
      toast.error('Failed to update phone');
    }
  };

  const copyToClipboard = (text, label) => {
    copyText(text, null).then((ok) => {
      if (!ok) return;
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const displayProfile = profile || {};

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="user-profile-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-[#d6deeb]">My Profile</h1>
        <p className="text-sm text-gray-500 dark:text-[#637777] mt-1">Manage your account</p>
      </div>

      <Tabs defaultValue="info" className="w-full">
        <TabsList className="w-full justify-start border-b rounded-none bg-transparent p-0 h-auto">
          <TabsTrigger
            value="info"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent px-4 py-2.5"
            data-testid="profile-tab-info"
          >
            <User className="w-4 h-4 mr-2" />
            Account Info
          </TabsTrigger>
        </TabsList>

        {/* ==================== Account Info Tab ==================== */}
        <TabsContent value="info" className="mt-6 space-y-4">
          <Card className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 dark:text-[#d6deeb]">
                <div className="h-12 w-12 rounded-full bg-blue-600 dark:bg-[#82aaff] flex items-center justify-center text-white dark:text-[#011627] text-xl font-bold">
                  {displayProfile.full_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="text-lg">{displayProfile.full_name || 'User'}</p>
                  <Badge variant="secondary" className="text-xs mt-0.5">
                    {displayProfile.role || 'Owner'}
                  </Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Email */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-[#011627]">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-400 dark:text-[#637777]" />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-[#637777]">Email</p>
                    <p className="font-medium text-gray-900 dark:text-[#d6deeb]">{displayProfile.email || 'Not set'}</p>
                  </div>
                </div>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => copyToClipboard(displayProfile.email, 'email')}
                  data-testid="copy-email-btn"
                >
                  {copied === 'email' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>

              {/* Phone */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-[#011627]">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400 dark:text-[#637777]" />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-[#637777]">Phone</p>
                    {editingPhone ? (
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          value={phoneValue}
                          onChange={(e) => setPhoneValue(e.target.value)}
                          placeholder="(555) 123-4567"
                          className="h-8 w-48"
                          data-testid="phone-input"
                        />
                        <Button size="sm" onClick={handleSavePhone} data-testid="save-phone-btn">Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingPhone(false)}>Cancel</Button>
                      </div>
                    ) : (
                      <p className="font-medium text-gray-900 dark:text-[#d6deeb]">
                        {displayProfile.phone || displayProfile.phone_number || 'Not set'}
                      </p>
                    )}
                  </div>
                </div>
                {!editingPhone && (
                  <Button variant="ghost" size="sm" onClick={() => setEditingPhone(true)} data-testid="edit-phone-btn">
                    Edit
                  </Button>
                )}
              </div>

              {/* Role */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-[#011627]">
                <Shield className="w-5 h-5 text-gray-400 dark:text-[#637777]" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-[#637777]">Role</p>
                  <p className="font-medium text-gray-900 dark:text-[#d6deeb] capitalize">{displayProfile.role || 'Owner'}</p>
                </div>
              </div>

              {/* Restaurant */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-[#011627]">
                <Building2 className="w-5 h-5 text-gray-400 dark:text-[#637777]" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-[#637777]">Restaurant</p>
                  <p className="font-medium text-gray-900 dark:text-[#d6deeb]">
                    {displayProfile.restaurant_name || restaurant?.name || 'Not set'}
                  </p>
                </div>
              </div>

              {/* Tenant ID */}
              {tenantId && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-[#011627]">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-gray-400 dark:text-[#637777]" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-[#637777]">Tenant ID</p>
                      <p className="font-mono text-sm text-gray-900 dark:text-[#d6deeb]">{tenantId}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(String(tenantId), 'tenant')}>
                    {copied === 'tenant' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cards and bank accounts live in Settings -> Billing (server side, through the Payment Hub) */}
        <Card className="mt-6 dark:bg-[#0b2942] dark:border-[#1d3b53]">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-[#d6deeb]">
              <CreditCard className="w-4 h-4" /> Payment methods for your subscription
            </div>
            <Button asChild size="sm" variant="outline" data-testid="profile-billing-link">
              <Link to={`${BO_BASE}/settings/billing`}>Manage in Billing</Link>
            </Button>
          </CardContent>
        </Card>
      </Tabs>

    </div>
  );
}
